// ============================================================================
// Exports « État de production » (Production V2) — PDF avec en-tête/pied OFFICIELS
// PoolIns (mêmes habillage et logos que les autres états), + export Excel.
// Réutilise drawEtatHeaderFooter / loadEntete / loadLogos des états sinistres.
// ============================================================================
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { frenchDateOnly } from "../../variables/functions";
import { displayNumbers } from "../../variables/numbersFunctions";
import {
    NAVY, loadEntete, loadLogos, drawEtatHeaderFooter, finalizeEtatPdf,
} from "../stats/sinistre/etatsSinistresFunctions";

const fd = (d) => (d ? frenchDateOnly(d) : "");
const fl = (v) => (v != null ? Math.floor(v) : "");
// jsPDF (police Helvetica) n'a pas le séparateur de milliers fr-FR (U+202F, espace fine insécable,
// + U+00A0 insécable) → il s'affiche comme un glyphe parasite (« / ») et casse l'alignement. On le
// remplace par une espace ASCII normale, uniquement pour le rendu PDF. String(...) gère le cas où
// displayNumbers renvoie 0 (number) ou "".
const pdfNum = (v) => String(displayNumbers(v) ?? "").replace(/[\u202F\u00A0]/g, " ");
const firstEngin = (it) => it?.engins?.[0];
const assureName = (it) => [it?.souscripteur?.nom, it?.souscripteur?.prenom].filter(Boolean).join(" ");
const apporteur = (it) => (it?.intermediaireNom ? `${it.intermediaireNom}${it?.intermediaireCode ? ` - ${it.intermediaireCode}` : ""}` : "");
// Libellés d'opération (code avenant → libellé) pour la colonne « Opération/Avenant ».
const AVENANT_LIBELLES = {
    AN: "Affaire nouvelle", RN: "Renouvellement", AG: "Augmentation", DG: "Diminution",
    IN: "Incorporation", PR: "Prorogation", TG: "Transfert", RV: "Remise en vigueur",
    RT: "Retrait véhicule", SP: "Suspension", RS: "Résiliation", AD: "Duplicata",
    AO: "Avenant d'ordre", CV: "Changement de véhicule", AA: "Avenant d'annulation",
};
const operationLabel = (it) => AVENANT_LIBELLES[it?.avenant] || it?.avenant || "";
// Aligné sur la liste V2/V3 : prime nette FRACTIONNÉE quand elle existe (≠ 0), sinon annuelle ;
// dans ce cas les accessoires sont inclus dans la P.N.
const estFractionne = (it) => !!(it?.primeNetteFractionnee && it.primeNetteFractionnee !== 0);
const pnEff = (it) => (estFractionne(it) ? it.primeNetteFractionnee : it?.primeNette);

// Colonnes communes PDF/écran. data = liste de ContratDTO (cf. /api/contrat/all).
const PDF_HEADERS = [
    "N° Police", "Immat.", "Assuré", "Opération", "Effet", "Échéance", "Souscription",
    "Prime nette", "Access.", "Taxes", "Prime TTC", "Apporteur",
];

const pdfRow = (it) => ([
    it?.numeroPoliceP ?? "",
    firstEngin(it)?.immatriculation ?? "",
    assureName(it),
    operationLabel(it),
    fd(it?.dateEffet),
    fd(it?.dateEcheance),
    fd(it?.dateSouscription),
    pdfNum(fl(pnEff(it))),
    estFractionne(it) ? "Incl. P.N." : pdfNum(fl(it?.accessoires)),
    pdfNum(fl(it?.primeTotale != null ? it.primeTotale * 0.06 / 1.06 : 0)),
    pdfNum(fl(it?.primeTotale)),
    apporteur(it),
]);

/**
 * État périodique des contrats — PDF officiel (en-tête + pied PoolIns + logos compagnies).
 * @param {Array} data  liste de ContratDTO
 * @param {Object} opts { title, periode:[start,end] (dayjs|Date|string), subtitle }
 * @returns {Promise<string>} datauristring (à ouvrir dans un nouvel onglet) ; sauvegarde aussi le fichier.
 */
export const generateContratsProductionPDF = async (data, { title, periode, subtitle, fileName, horsPool = false } = {}) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    // Totaux de pied de tableau
    const totals = (data || []).reduce((acc, it) => {
        // Prime nette EFFECTIVE (fractionnée si présente) ; accessoires non comptés quand ils
        // sont déjà inclus dans la P.N. fractionnée (sinon double comptage).
        acc.pn += Number(pnEff(it)) || 0;
        acc.acc += estFractionne(it) ? 0 : (Number(it?.accessoires) || 0);
        acc.tx += (Number(it?.primeTotale) || 0) * 0.06 / 1.06;
        acc.ttc += Number(it?.primeTotale) || 0;
        acc.pnc += Number(it?.primeNetteCompagnie) || 0;
        // Prime nette ANNUELLE (pour la répartition Pool / compagnie, indépendante du fractionnement).
        acc.pnAnnuel += Number(it?.primeNette) || 0;
        return acc;
    }, { pn: 0, acc: 0, tx: 0, ttc: 0, pnc: 0, pnAnnuel: 0 });

    const stB = { halign: "right", fontStyle: "bold" }; // style total
    const tBold = (v) => ({ content: pdfNum(Math.floor(v)), styles: stB });

    let headers, rows, totalRow;
    if (horsPool) {
        // Onglet « Garanties hors Pool » : 2 colonnes supplémentaires (part compagnie + garanties).
        headers = [...PDF_HEADERS, "Prime Cie", "Garanties hors Pool"];
        rows = (data || []).map((it) => [...pdfRow(it), pdfNum(fl(it?.primeNetteCompagnie)), it?.garantiesHorsPool ?? ""]);
        totalRow = [
            { content: `TOTAL — ${(data || []).length} contrat(s)`, colSpan: 7, styles: stB },
            tBold(totals.pn), tBold(totals.acc), tBold(totals.tx), tBold(totals.ttc), "",
            tBold(totals.pnc), "",
        ];
    } else {
        // État standard (/production v2 & v3) : répartition PN Pool / PN compagnie + statut de règlement.
        // PN Pool / PN compagnie sont ANNUELS (nette − part compagnie) — la colonne « Prime nette »
        // reste, elle, la prime EFFECTIVE (fractionnée si applicable).
        headers = ["N° Police", "Immat.", "Assuré", "Opération", "Effet", "Échéance", "Souscription",
            "Prime nette", "PN Pool", "PN Cie", "Access.", "Taxes", "Prime TTC", "Statut", "Apporteur"];
        rows = (data || []).map((it) => {
            const b = pdfRow(it); // 0..6 identiques · 7 nette · 8 access · 9 taxes · 10 ttc · 11 apporteur
            const pnPool = (Number(it?.primeNette) || 0) - (Number(it?.primeNetteCompagnie) || 0);
            return [b[0], b[1], b[2], b[3], b[4], b[5], b[6], b[7],
                pdfNum(fl(pnPool)), pdfNum(fl(it?.primeNetteCompagnie)),
                b[8], b[9], b[10], it?.statutReglement ?? "", b[11]];
        });
        totalRow = [
            { content: `TOTAL — ${(data || []).length} contrat(s)`, colSpan: 7, styles: stB },
            tBold(totals.pn), tBold(totals.pnAnnuel - totals.pnc), tBold(totals.pnc),
            tBold(totals.acc), tBold(totals.tx), tBold(totals.ttc), "", "",
        ];
    }
    rows.push(totalRow);

    const periodeLabel = (Array.isArray(periode) && periode[0] && periode[1])
        ? `DU ${fd(periode[0])} AU ${fd(periode[1])}` : "";

    autoTable(doc, {
        head: [headers],
        body: rows,
        theme: "grid",
        // 6,5 pt pour les deux : l'état standard a désormais 15 colonnes (répartition PN + statut).
        styles: { fontSize: 6.5, cellPadding: 1.3, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: "linebreak", valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        alternateRowStyles: { fillColor: [246, 247, 251] },
        margin: { top: 32, bottom: 20, left: 14, right: 14 },
        columnStyles: horsPool ? {
            // 14 colonnes : largeurs auto, seuls les alignements numériques sont fixés.
            7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" },
            10: { halign: "right" }, 12: { halign: "right" },
        } : {
            // 15 colonnes : ...7 nette · 8 PN Pool · 9 PN Cie · 10 access · 11 taxes · 12 ttc · 13 statut...
            0: { cellWidth: 22 }, 2: { cellWidth: 28 },
            7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" },
            10: { halign: "right" }, 11: { halign: "right" }, 12: { halign: "right" },
            13: { halign: "center" },
        },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `${title || "ÉTAT DE PRODUCTION"}${periodeLabel ? ` ${periodeLabel}` : ""}`,
            subtitle: subtitle || "",
            enteteData, logos, formattedNow,
        }),
    });

    finalizeEtatPdf(doc);
    doc.save(`${fileName || "EtatProduction"}_${formattedNow.replace(/[/:\s]/g, "-")}.pdf`);
    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

/**
 * Bordereau de CESSION (Émissions + Annulations) — PDF officiel (mêmes en-tête/pied/logos).
 * Reçoit EXACTEMENT les mêmes données que l'export Excel « bordereau (cession) » (source /cession) :
 * lignes déjà aplaties { numeroPolice, avenant, assure, immatriculation, dateEmission, agence,
 * intermediaire, effet, echeance, categorie, primeNettePool, primeNetteCompagnie, accessoires,
 * taxes, primeACeder }. Montants tronqués en valeur absolue ; dernière colonne « Prime TTC ».
 */
export const generateBordereauCessionPDF = async ({ emissions = [], annulations = [], moisLabel = "", compagnie = "", branche = "" } = {}) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);

    const fdate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "");
    const tr = (v) => pdfNum(Math.trunc(Math.abs(Number(v) || 0)));
    const som = (list, k) => Math.trunc((list || []).reduce((s, it) => s + Math.abs(Number(it[k]) || 0), 0));
    const stB = { halign: "right", fontStyle: "bold" };
    const bandStyles = { halign: "left", fontStyle: "bold", fillColor: [230, 231, 240], textColor: NAVY };

    const subtitle = [compagnie ? `Compagnie : ${compagnie}` : "", branche ? `Branche : ${branche}` : ""]
        .filter(Boolean).join("   ·   ");
    const baseOpts = {
        theme: "grid",
        styles: { fontSize: 6.5, cellPadding: 1.2, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: "linebreak", valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        alternateRowStyles: { fillColor: [246, 247, 251] },
        margin: { top: 32, bottom: 20, left: 14, right: 14 },
        didDrawPage: () => drawEtatHeaderFooter(doc, {
            title: `BORDEREAU DE CESSION${moisLabel ? ` — ${moisLabel}` : ""}`,
            subtitle, enteteData, logos, formattedNow,
        }),
    };

    // ── ÉMISSIONS (15 colonnes) ──
    const emiCols = ["N° Police", "Avenant", "Assuré", "Immat.", "Date émission", "Point de vente",
        "Intermédiaire", "Effet", "Échéance", "Catég.", "PN Pool", "PN Cie", "Access.", "Taxes", "Prime TTC"];
    const emiBody = emissions.map((it) => [
        it.numeroPolice, it.avenant, it.assure, it.immatriculation || "", fdate(it.dateEmission),
        it.agence || "", it.intermediaire || "", fdate(it.effet), fdate(it.echeance), it.categorie,
        tr(it.primeNettePool), tr(it.primeNetteCompagnie), tr(it.accessoires), tr(it.taxes), tr(it.primeACeder),
    ]);
    if (emiBody.length) emiBody.push([
        { content: "TOTAL", colSpan: 10, styles: stB },
        { content: pdfNum(som(emissions, "primeNettePool")), styles: stB },
        { content: pdfNum(som(emissions, "primeNetteCompagnie")), styles: stB },
        { content: pdfNum(som(emissions, "accessoires")), styles: stB },
        { content: pdfNum(som(emissions, "taxes")), styles: stB },
        { content: pdfNum(som(emissions, "primeACeder")), styles: stB },
    ]);
    autoTable(doc, {
        ...baseOpts, startY: 34,
        head: [[{ content: "ÉMISSIONS", colSpan: 15, styles: bandStyles }], emiCols],
        body: emiBody.length ? emiBody : [[{ content: "Aucune émission", colSpan: 15, styles: { halign: "center", textColor: [120, 120, 120] } }]],
        columnStyles: { 10: { halign: "right" }, 11: { halign: "right" }, 12: { halign: "right" }, 13: { halign: "right" }, 14: { halign: "right" } },
    });

    // ── ANNULATIONS (11 colonnes) — enchaîne sous les émissions ──
    const annCols = ["N° Police", "Avenant", "Assuré", "Effet", "Échéance", "Catég.",
        "PN Pool", "PN Cie", "Access.", "Taxes", "Prime TTC"];
    const annBody = annulations.map((it) => [
        it.numeroPolice, it.avenant, it.assure, fdate(it.effet), fdate(it.echeance), it.categorie,
        tr(it.primeNettePool), tr(it.primeNetteCompagnie), tr(it.accessoires), tr(it.taxes), tr(it.primeACeder),
    ]);
    if (annBody.length) annBody.push([
        { content: "TOTAL", colSpan: 6, styles: stB },
        { content: pdfNum(som(annulations, "primeNettePool")), styles: stB },
        { content: pdfNum(som(annulations, "primeNetteCompagnie")), styles: stB },
        { content: pdfNum(som(annulations, "accessoires")), styles: stB },
        { content: pdfNum(som(annulations, "taxes")), styles: stB },
        { content: pdfNum(som(annulations, "primeACeder")), styles: stB },
    ]);
    autoTable(doc, {
        ...baseOpts, startY: (doc.lastAutoTable?.finalY || 34) + 8,
        head: [[{ content: "ANNULATIONS", colSpan: 11, styles: bandStyles }], annCols],
        body: annBody.length ? annBody : [[{ content: "Aucune annulation", colSpan: 11, styles: { halign: "center", textColor: [120, 120, 120] } }]],
        columnStyles: { 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right" } },
    });

    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

/**
 * TABLEAU DE RÉPARTITION DES PRIMES À REPORTER (PREC) — PDF officiel (en-tête/pied/logos PoolIns).
 * Reçoit le récap tel que renvoyé par /api/stats/primes-a-reporter :
 * { exercice, dateLabel, branches[], totauxParBranche{}, totalGeneral, repartition[] }.
 */
export const generatePrimesAReporterPDF = async ({ exercice, dateLabel = "", branches = [], totauxParBranche = {}, totauxNetteParBranche = {}, totauxAccessoiresParBranche = {}, totalGeneral = 0, repartition = [] } = {}) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);
    const money = (v) => pdfNum(Math.trunc(Number(v) || 0));
    const pct = (v) => (v != null && v !== "" ? `${(+v).toLocaleString("fr-FR")}%` : "");
    const stB = { fontStyle: "bold" };
    const stBR = { fontStyle: "bold", halign: "right" };

    const drawPage = () => drawEtatHeaderFooter(doc, {
        title: "TABLEAU DE RÉPARTITION DES PRIMES À REPORTER",
        subtitle: `Exercice ${exercice}${dateLabel ? `   ·   ${dateLabel}` : "   ·   exercice complet"}`,
        enteteData, logos, formattedNow,
    });
    const baseStyles = {
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.4, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: "linebreak", valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 7, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        margin: { top: 32, bottom: 20, left: 14, right: 14 },
        didDrawPage: drawPage,
    };

    // ── Bloc « PRIME TOTALE À REPORTER » par branche (total + ventilation nette / accessoires) ──
    const sumMap = (m) => Object.values(m || {}).reduce((a, v) => a + (Number(v) || 0), 0);
    autoTable(doc, {
        ...baseStyles, startY: 34,
        head: [[
            { content: "PRIME TOTALE À REPORTER", styles: { halign: "left" } },
            { content: "TOTAL", styles: { halign: "right" } },
            { content: "dont NETTE", styles: { halign: "right" } },
            { content: "dont ACCESSOIRES", styles: { halign: "right" } },
        ]],
        body: [
            ...branches.map((br) => [
                { content: br, styles: stB },
                { content: money(totauxParBranche[br]), styles: { halign: "right" } },
                { content: money(totauxNetteParBranche[br]), styles: { halign: "right" } },
                { content: money(totauxAccessoiresParBranche[br]), styles: { halign: "right" } },
            ]),
            [
                { content: "TOTAL", styles: stB },
                { content: money(totalGeneral), styles: stBR },
                { content: money(sumMap(totauxNetteParBranche)), styles: stBR },
                { content: money(sumMap(totauxAccessoiresParBranche)), styles: stBR },
            ],
        ],
        columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 35, halign: "right" }, 2: { cellWidth: 35, halign: "right" }, 3: { cellWidth: 40, halign: "right" } },
        tableWidth: 150,
    });

    // ── Tableau de répartition (compagnie × branche : quote-part + montant) ──
    // Ligne TOTAUX : quote-part cumulée par branche + montants = totaux par branche.
    const qpTot = {};
    branches.forEach((br) => {
        qpTot[br] = (repartition || []).reduce((s, c) => s + (Number(c.quotePartParBranche?.[br]) || 0), 0);
    });

    const head1 = [{ content: "SOCIÉTÉS D'ASSURANCES", rowSpan: 2, styles: { valign: "middle" } }];
    branches.forEach((br) => head1.push({ content: br, colSpan: 2, styles: { halign: "center" } }));
    head1.push({ content: "TOTAL PAR COMPAGNIE", rowSpan: 2, styles: { valign: "middle", halign: "right" } });
    const head2 = [];
    branches.forEach(() => head2.push({ content: "QUOTE PART", styles: { halign: "center" } }, { content: "MONTANT", styles: { halign: "right" } }));

    const body = (repartition || []).map((c) => {
        const line = [c.compagnieNom || c.compagnieCode || ""];
        branches.forEach((br) => {
            line.push({ content: pct(c.quotePartParBranche?.[br]), styles: { halign: "center" } });
            line.push({ content: money(c.montantParBranche?.[br]), styles: { halign: "right" } });
        });
        line.push({ content: money(c.totalCompagnie), styles: stBR });
        return line;
    });
    // TOTAUX
    const totalLine = [{ content: "TOTAUX", styles: stB }];
    branches.forEach((br) => {
        totalLine.push({ content: pct(qpTot[br]), styles: { halign: "center", fontStyle: "bold" } });
        totalLine.push({ content: money(totauxParBranche[br]), styles: stBR });
    });
    totalLine.push({ content: money(totalGeneral), styles: stBR });
    body.push(totalLine);

    autoTable(doc, {
        ...baseStyles, startY: (doc.lastAutoTable?.finalY || 34) + 8,
        head: [head1, head2],
        body,
        columnStyles: { 0: { cellWidth: 45, fontStyle: "bold" } },
    });

    // ── Méthode + NB + clés ──
    let y = (doc.lastAutoTable?.finalY || 34) + 8;
    doc.setFontSize(7.5).setFont("helvetica", "bold").setTextColor(45, 45, 45);
    doc.text("Méthode de calcul : Prorata temporis — assiette = prime nette + accessoires.", 14, y);
    doc.text("NB : il revient à chaque société membre du Pool d'appliquer le taux approprié à la prime à reporter pour obtenir la PREC à provisionner.", 14, y + 5);
    doc.setFont("helvetica", "normal");
    doc.text(`Clés utilisées : clés de l'exercice ${exercice}.`, 14, y + 10);

    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

/**
 * Bordereau CONSOLIDÉ de rétrocession — PDF officiel (en-tête/pied/logos PoolIns).
 * @param {Object} o { titre, sousTitre, compagnies:[{code,raisonSociale}], rows:[{numeroPolice,cedante,
 *   echeance, primeCedee, totalRetrocede, parCompagnie:{[code]:montant}}], totalRetrocede }
 */
export const generateRetrocessionBordereauPDF = async ({ titre = "", sousTitre = "", compagnies = [], rows = [], totalRetrocede = 0 } = {}) => {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const formattedNow = new Date().toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
    const [enteteData, logos] = await Promise.all([loadEntete(), loadLogos()]);
    const money = (v) => pdfNum(Math.trunc(Number(v) || 0));
    const stB = { fontStyle: "bold", halign: "right" };

    // Compat : chaque cellule par compagnie peut être un nombre (ancien) ou { taux, montant } (nouveau).
    const cellMontant = (v) => (v && typeof v === "object") ? (Number(v.montant) || 0) : (Number(v) || 0);
    const cellTaux = (v) => (v && typeof v === "object") ? (Number(v.taux) || 0) : 0;
    const pct = (v) => `${(Number(v) || 0).toFixed(2)}%`;

    // En-tête à 2 niveaux : nom de compagnie (colSpan 2) au-dessus de « Taux | Rétrocédé ».
    const head1 = [
        { content: "N° Police", rowSpan: 2 },
        { content: "Cédante", rowSpan: 2 },
        { content: "Échéance", rowSpan: 2 },
        { content: "Prime cédée", rowSpan: 2 },
        ...compagnies.map((c) => ({ content: c.raisonSociale || c.compagnieNom || c.code || "", colSpan: 2, styles: { halign: "center" } })),
        { content: "Total Rétrocédé", rowSpan: 2 },
    ];
    const head2 = compagnies.flatMap(() => [
        { content: "Taux", styles: { halign: "right" } },
        { content: "Rétrocédé", styles: { halign: "right" } },
    ]);
    const body = rows.map((r) => [
        r.numeroPolice || "", r.cedante || "", r.echeance || "", money(r.primeCedee),
        ...compagnies.flatMap((c) => {
            const v = r.parCompagnie?.[c.code];
            return [pct(cellTaux(v)), money(cellMontant(v))];
        }),
        money(r.totalRetrocede),
    ]);
    const sum = (f) => rows.reduce((s, r) => s + (Number(f(r)) || 0), 0);
    const total = [
        { content: "TOTAL", colSpan: 3, styles: { fontStyle: "bold" } },
        { content: money(sum((r) => r.primeCedee)), styles: stB },
        ...compagnies.flatMap((c) => [
            { content: "", styles: stB },
            { content: money(sum((r) => cellMontant(r.parCompagnie?.[c.code]))), styles: stB },
        ]),
        { content: money(totalRetrocede), styles: stB },
    ];
    if (body.length) body.push(total);

    const numCols = { 3: { halign: "right" } };
    compagnies.forEach((_, i) => {
        numCols[4 + i * 2] = { halign: "right" };       // Taux
        numCols[4 + i * 2 + 1] = { halign: "right" };   // Rétrocédé
    });
    numCols[4 + compagnies.length * 2] = { halign: "right", fontStyle: "bold" };

    autoTable(doc, {
        head: [head1, head2], body, theme: "grid", startY: 34,
        styles: { fontSize: 6.5, cellPadding: 1.1, lineWidth: 0.1, lineColor: [215, 215, 225], textColor: [45, 45, 45], overflow: "linebreak", valign: "middle" },
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 6.5, fontStyle: "bold", lineWidth: 0.1, lineColor: NAVY },
        alternateRowStyles: { fillColor: [246, 247, 251] },
        margin: { top: 32, bottom: 20, left: 10, right: 10 },
        columnStyles: numCols,
        didDrawPage: () => drawEtatHeaderFooter(doc, { title: titre, subtitle: sousTitre, enteteData, logos, formattedNow }),
    });
    finalizeEtatPdf(doc);
    return doc.output("datauristring");
};

// Export Excel (même jeu de colonnes que le tableau), basé sur ContratDTO.
export const generateContratsProductionExcel = (data, fileName = "EtatProduction") => {
    const headers = [
        "N° Police", "Immatriculation", "Assuré", "Date effet", "Date échéance", "Date souscription",
        "Prime nette", "Accessoires", "Taxes", "Prime TTC", "Apporteur", "Concessionnaire",
    ];
    const aoa = [headers];
    for (const it of (data || [])) {
        aoa.push([
            it?.numeroPoliceP ?? "",
            firstEngin(it)?.immatriculation ?? "",
            assureName(it),
            fd(it?.dateEffet),
            fd(it?.dateEcheance),
            fd(it?.dateSouscription),
            fl(it?.primeNette),
            fl(it?.accessoires),
            fl(it?.primeTotale != null ? it.primeTotale * 0.06 / 1.06 : 0),
            fl(it?.primeTotale),
            apporteur(it),
            it?.concessionnaire ?? "",
        ]);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Production");
    const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([blob], { type: "application/octet-stream" }), `${fileName}.xlsx`);
};
